import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function OndtIRyggenPage() {
  const spineAreas = [
    {
      title: "Lændesmerter & hekseskud (lumbago)",
      desc: "Pludseligt opstået hold i ryggen eller dyb, murrende smerte i lænden, som gør det svært at rejse sig eller bøje sig fremover.",
      icon: "fa-solid fa-person-walking",
    },
    {
      title: "Iskias & udstrålende nervesmerter",
      desc: "Afklemning eller irritation af iskiasnerven med jagende smerter, snurren eller følelsesløshed ned i balden, låret og læggen.",
      icon: "fa-solid fa-bolt-lightning",
    },
    {
      title: "Stivhed mellem skulderbladene",
      desc: "Låsninger i brystryggen og facetleddene forårsaget af siddende arbejde, som gør det smertefuldt at trække vejret helt ned i maven.",
      icon: "fa-solid fa-shield-halved",
    },
    {
      title: "Kroniske rygbelastninger",
      desc: "Langvarig slidgigt, discusprolaps-eftervirkninger eller asymmetriske arbejdsstillinger, der holder ryggens muskler i konstant krampe.",
      icon: "fa-solid fa-bone",
    },
  ];

  const benefits = [
    "Dæmper akutte og kroniske smerter i lænd og ryg",
    "Mindsker tryk på iskiasnerven og dæmper udstråling",
    "Løsner dybe kramper i de stabiliserende rygmuskler",
    "Stimulerer lokal blodgennemstrømning og kroppens restitution",
    "Reducerer behovet for smertestillende medicin",
    "Giver dig bevægelsesfriheden og en velfungerende hverdag tilbage",
  ];

  const conversationQuestions = [
    {
      title: "Smerternes debut & variation",
      desc: "Hvornår smerterne begyndte, og om de ændrer sig i løbet af dagen",
      icon: "fa-regular fa-clock",
    },
    {
      title: "Udløsende bevægelser",
      desc: "Hvilke bevægelser eller stillinger der gør ondt",
      icon: "fa-solid fa-person-walking",
    },
    {
      title: "Hverdagens påvirkning",
      desc: "Hvordan ryggen påvirker din søvn, dit arbejde og din fritid",
      icon: "fa-solid fa-bed",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Akupunktur mod Rygsmerter | Risskov & Aarhus</title>
        <meta
          name="description"
          content="Effektiv lindring af rygsmerter, hekseskud og lændesmerter med akupunktur i Risskov. RAB-godkendt behandling der løsner og genopretter kroppen."
        />
        <meta
          property="og:title"
          content="Akupunktur mod Rygsmerter | Risskov & Aarhus"
        />
        <meta
          property="og:description"
          content="Effektiv lindring af rygsmerter, hekseskud og lændesmerter med akupunktur i Risskov. RAB-godkendt behandling der løsner og genopretter kroppen."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/ondt-i-ryggen" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Ondt i ryggen og lænden - Akupunktur i Risskov og Jystrup",
            "url": "https://kita-akupunktur.dk/ondt-i-ryggen",
            "description":
              "Individuelt tilpasset japansk akupunktur ved ondt i ryggen, lændesmerter og iskias i Risskov ved Aarhus, også for klienter fra Jystrup.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={0} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li>
                <Link to="/akupunktur" className="hover:text-[#d74814] transition-colors">
                  Akupunktur
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Ondt i ryggen og lænden
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>FOKUSOMRÅDE • RYG, LÆND & ISKIAS</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] max-w-4xl leading-tight">
            Ondt i ryggen og lænden? Akupunktur for dig fra Risskov og Jystrup
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] mt-4 max-w-3xl leading-relaxed">
            Har du ondt i ryggen og lænden, kan selv små ting som at rejse dig, gå en tur eller finde ro om natten blive besværlige. Hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            i Risskov ved Aarhus er du også velkommen fra Jystrup. Jeg tilbyder individuelt tilpasset akupunktur med fokus på dine gener, din følsomhed og det, du gerne vil kunne i hverdagen.
          </p>

          {/* Quick Info Badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
            >
              <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
              <span>Bestil tid til ryggen</span>
            </Link>

            <a
              href="tel:+4529387186"
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white/90 text-[#593427] text-sm font-medium hover:bg-white border border-[#ffe0cf] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
              <span>Tlf. 29 38 71 86</span>
            </a>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427]">
              <i className="fa-solid fa-square-parking text-[#593427]" aria-hidden="true" />
              <span>Gratis parkering ved klinikken</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 sm:py-20 bg-[#fffaf7] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* Section: Når du har ondt i ryggen, er din hverdag vigtig */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-[0_8px_30px_rgba(89,52,39,0.05)] relative overflow-hidden">
            <div className="max-w-4xl space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>SAMTALE & DINE OPLEVELSER</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Når du har ondt i ryggen, er din hverdag vigtig
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                <p>
                  Måske mærker du stivhed efter en arbejdsdag, eller måske gør det ondt at bøje dig og tage sko på. Har du især ondt i ryggen omkring lænden, vil jeg gerne høre, hvordan smerterne påvirker dig. Det giver et bedre udgangspunkt for vores samtale end blot at vide, hvor smerten sidder.
                </p>
                <p>
                  Rygsmerter kan have flere årsager, og det er ikke altid muligt at pege på én bestemt forklaring. Nye, stærke eller ændrede smerter bør vurderes af en læge. Hos mig tager samtalen udgangspunkt i dine oplevelser og den udredning, du eventuelt har fået. Jeg spørger blandt andet ind til følgende:
                </p>
              </div>

              {/* 3 Spørgsmål kort */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {conversationQuestions.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-5 rounded-2xl bg-[#fff5ee] border border-[#ffe0cf] space-y-2 flex flex-col justify-start"
                  >
                    <div className="w-9 h-9 rounded-xl bg-white border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className={item.icon} aria-hidden="true" />
                    </div>
                    <h3 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section: Hvad indebærer akupunktur mod smerter for dig fra Aarhus eller Jystrup? */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>RAB-REGISTRERET AKUPUNKTØR</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Hvad indebærer akupunktur mod smerter for dig fra Aarhus eller Jystrup?
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base">
                <p>
                  Hos mig tilbydes akupunktur mod smerter med symptomlindring som formål. Der er ingen garanti for, at behandlingen hjælper dig, og den erstatter ikke lægelig udredning, relevant træning eller anden nødvendig behandling. Vi taler derfor også om, hvad du allerede gør for din ryg, og hvad du forventer af forløbet.
                </p>
                <p>
                  Jeg hedder <strong>Mayumi Kita Thiele</strong> og er registreret alternativ behandler, RAB. Min faglige profil er især japansk akupunktur, og jeg har også klinisk videreuddannelse fra Japan. Hos mig møder du en personlig tilgang, hvor dine spørgsmål og reaktioner er en del af behandlingen.
                </p>
                <p>
                  Akupunktur er anerkendt verden over for sin evne til at stimulere nervesystemet, frigive kroppens egne smertelindrende stoffer og fremme blodcirkulationen omkring overbelastede muskler og led.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white/95 p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-[0_8px_30px_rgba(89,52,39,0.06)] space-y-4">
              <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2 border-b border-[#ffe0cf]/80 pb-3">
                <i className="fa-solid fa-check-double text-[#d74814]" aria-hidden="true" />
                <span>Fokus i ryg- og lændebehandlingen:</span>
              </h3>
              <ul className="space-y-2.5 text-sm text-[#7a5b4f]">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start space-x-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-2">
                <div className="p-3.5 rounded-xl bg-[#ffe0cf]/30 border border-[#ffe0cf] text-xs text-[#593427] flex items-center space-x-3">
                  <JapaneseBadge className="shrink-0" />
                  <span>
                    RAB-godkendt: Mulighed for tilskud via Sygeforsikringen "danmark" eller privat sundhedsforsikring.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Japansk akupunktur tilpasset din følsomhed */}
          <div className="bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-6">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>NÆNSOMMET & TRYGHED</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Japansk akupunktur tilpasset din følsomhed
              </h2>
              <p className="text-base sm:text-lg text-[#7a5b4f] leading-relaxed">
                Ved japansk akupunktur bruger jeg meget tynde, sterile engangsnåle, blandt andet med en tykkelse på 0,12 mm. Jeg tilpasser teknik og indstik efter din følsomhed, og du kan sige til undervejs. Du behøver ikke kende metoden på forhånd for at fortælle mig, hvad der føles trygt for dig.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch pt-2">
              {/* Card 1: Tynde nåle */}
              <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3 flex flex-col justify-between shadow-2xs">
                <div className="space-y-3">
                  <div className="inline-flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                    <i className="fa-solid fa-circle-check text-xs" aria-hidden="true" />
                    <span>Meget tynde nåle (helt ned til 0,12 mm)</span>
                  </div>
                  <h3 className="font-serif font-semibold text-base text-[#593427]">
                    Skånsom og præcis stimulering
                  </h3>
                  <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                    Den japanske tradition lægger vægt på, at kroppen skal slappe af under behandlingen frem for at overvældes. De ultrafine nåle giver en blid stimulering, der effektivt dæmper spændinger uden unødig smerte.
                  </p>
                </div>
                <div className="pt-2 border-t border-[#ffe0cf]/60 text-xs text-[#7a5b4f] italic">
                  Jeg lover ikke, at nålebehandling aldrig kan mærkes – men det skal altid føles trygt.
                </div>
              </div>

              {/* Card 2: Kontaktnåle */}
              <div className="p-6 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-3 flex flex-col justify-between shadow-2xs">
                <div className="space-y-3">
                  <div className="inline-flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                    <i className="fa-solid fa-hand-holding-heart text-xs" aria-hidden="true" />
                    <span>Utryg ved nåle? Mulighed for kontaktnåle</span>
                  </div>
                  <h3 className="font-serif font-semibold text-base text-[#593427]">
                    Berøring uden at trænge ind i huden
                  </h3>
                  <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                    Hvis du er utryg ved nåle, vil jeg gerne vide det inden behandlingen. Der er mulighed for at bruge kontaktnåle, som berører huden uden at trænge ind. Vi taler om mulighederne og dine ønsker, så du ved, hvad der skal ske.
                  </p>
                </div>
                <div className="pt-2 border-t border-[#ffe0cf]/60 text-xs text-[#7a5b4f] italic">
                  Du har fuld kontrol under hele forløbet og kan altid sige til undervejs.
                </div>
              </div>
            </div>
          </div>

          {/* Section: Typiske rygproblemer jeg behandler */}
          <div className="space-y-6">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Typiske rygproblemer jeg behandler
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f]">
                Hver ryg er unik. Derfor vurderer vi altid årsagen til dine smerter nøje før selve behandlingen.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {spineAreas.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white/90 p-6 rounded-2xl border border-[#ffe0cf] hover:border-[#d74814]/40 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="w-11 h-11 rounded-xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className={`${item.icon} text-lg`} aria-hidden="true" />
                    </div>
                    <h3 className="font-serif font-semibold text-base text-[#593427]">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section: Dit forløb ved ondt i ryggen og lænden */}
          <div className="glass-card p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-6">
            <div className="flex items-center space-x-3 border-b border-[#ffe0cf]/70 pb-4">
              <div className="w-9 h-9 rounded-full bg-[#ffe0cf]/70 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Dit forløb ved ondt i ryggen og lænden
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                <p>
                  Den første samtale handler om dine gener, dit helbred og dine ønsker til forløbet. Ved japansk akupunktur kan en traditionel vurdering af puls og mave indgå i valget af punkter. Det er en del af akupunkturens metodegrundlag og må ikke forveksles med en lægelig undersøgelse eller diagnose.
                </p>
                <p>
                  Ved de næste besøg følger vi op på, hvordan du har haft det efter behandlingen. Har smerterne ændret sig, og er bestemte ting blevet lettere eller sværere? Jeg justerer behandlingen ud fra dine reaktioner. Vi aftaler forløbet individuelt frem for at tage udgangspunkt i et fast antal behandlinger.
                </p>
              </div>

              <div className="lg:col-span-5 space-y-3">
                <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-start space-x-3 shadow-2xs">
                  <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                    1
                  </span>
                  <div>
                    <h4 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                      Første samtale & afdækning
                    </h4>
                    <p className="text-xs text-[#7a5b4f] mt-0.5">
                      Gennemgang af dine gener, helbred og puls/mave-vurdering til valg af akupunkturpunkter.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-start space-x-3 shadow-2xs">
                  <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                    2
                  </span>
                  <div>
                    <h4 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                      Løbende opfølgning
                    </h4>
                    <p className="text-xs text-[#7a5b4f] mt-0.5">
                      Vi evaluerer ændringer i hverdagens gøremål, smertestyrke og søvnkvalitet.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-start space-x-3 shadow-2xs">
                  <span className="w-7 h-7 rounded-full bg-[#ffe0cf] text-[#d74814] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                    3
                  </span>
                  <div>
                    <h4 className="font-serif font-semibold text-[#593427] text-sm sm:text-base">
                      Individuel plan
                    </h4>
                    <p className="text-xs text-[#7a5b4f] mt-0.5">
                      Ingen låste pakkeforløb – vi tilpasser antallet af behandlinger efter dine behov.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Iskias Special Callout */}
          <div className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-8 space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>SÆRLIGT OM ISKIAS</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Trækker smerten ned i ballen eller benet?
                </h3>
                <p className="text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                  Iskiassmerter skyldes oftest, at iskiasnerven bliver klemt af spændte sædemuskler (f.eks. piriformis-syndrom) eller en bule på en bruskskive i lænden. Akupunktur er exceptionelt godt til at ramme de dybe ballemuskler og dæmpe den akutte nerveirritation, så smerterne opad og nedad i benet klinger af.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs text-center"
                >
                  <span>Bestil tid til iskiashjælp</span>
                  <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:+4529387186"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-white text-[#593427] text-sm font-medium hover:bg-[#fff5ee] border border-[#ffe0cf] transition-colors text-center"
                >
                  <span>Ring på 29 38 71 86</span>
                </a>
              </div>
            </div>
          </div>

          {/* Section: Hvad kan du selv gøre, når ryggen gør ondt? */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-6">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>EGENOMSORG & RÅD</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Hvad kan du selv gøre, når ryggen gør ondt?
              </h2>
              <p className="text-base sm:text-lg text-[#7a5b4f] leading-relaxed">
                Ved almindelige rygsmerter er det som regel en god idé at holde sig i gang i det omfang, man kan. Korte gåture og variation mellem at sidde, stå og bevæge sig kan være en start. Følg de råd, du har fået af din læge eller fysioterapeut, og få vejledning, hvis du er usikker på, hvilken aktivitet der passer til dig.
              </p>
            </div>

            {/* Vigtig boks om akut lægehjælp */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#fff2ec] border border-[#d74814]/30 space-y-3">
              <div className="flex items-start space-x-3">
                <i className="fa-solid fa-triangle-exclamation text-[#d74814] text-xl mt-1 shrink-0" aria-hidden="true" />
                <div className="space-y-2">
                  <h3 className="font-serif font-semibold text-lg text-[#593427]">
                    Hvornår bør du søge akut lægehjælp?
                  </h3>
                  <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                    Søg akut lægehjælp ved rygsmerter sammen med nye problemer med at lade vandet, holde på afføring, følelsesløshed omkring skridtet eller tydelig svaghed i benene. Kontakt også lægen ved feber, smerter efter en større skade eller vedvarende forværring. Her skal du ikke vente på en tid til akupunktur.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Kontakt mig om akupunktur i Risskov, også for dig fra Jystrup */}
          <div className="bg-gradient-to-br from-white via-[#fffaf7] to-[#fff5ee] p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>KLINIKKEN I RISSKOV VED AARHUS</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                  Kontakt mig om akupunktur i Risskov, også for dig fra Jystrup
                </h2>
                <div className="space-y-3 text-[#7a5b4f] leading-relaxed text-sm sm:text-base">
                  <p>
                    Har du ondt i ryggen og bor i Aarhus eller Jystrup, er du velkommen i min klinik på <strong>Kantorparken 11 i Risskov</strong>. Der er gratis parkering foran huset, og behandling foregår efter aftale. Du er velkommen til at spørge til min tilgang, også hvis du ikke tidligere har prøvet akupunktur og gerne vil vide mere først.
                  </p>
                  <p>
                    Du kan ringe til mig på <a href="tel:+4529387186" className="text-[#d74814] font-semibold hover:underline">29 38 71 86</a> eller kontakte mig for at aftale en tid. Fortæl gerne kort om dine rygsmerter, og om du allerede er undersøgt eller får anden behandling. Så kan vi tale om dine forventninger og afklare, om du ønsker at begynde et forløb hos mig.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-4 flex flex-col gap-3 justify-center">
                <a
                  href="tel:+4529387186"
                  className="inline-flex items-center justify-center space-x-2 px-5 py-3 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs text-center"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  <span>Ring 29 38 71 86</span>
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center space-x-2 px-5 py-3 rounded-xl bg-white text-[#593427] text-sm font-medium hover:bg-[#fff5ee] border border-[#ffe0cf] transition-colors text-center"
                >
                  <i className="fa-regular fa-envelope text-xs" aria-hidden="true" />
                  <span>Send en besked</span>
                </Link>
                <div className="p-3 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427] text-center">
                  <i className="fa-solid fa-location-dot text-[#d74814] mr-1.5" aria-hidden="true" />
                  <span>Kantorparken 11, 8240 Risskov</span>
                </div>
              </div>
            </div>
          </div>

          {/* Related pages links */}
          <div className="p-6 rounded-2xl bg-[#fff5ee] border border-[#ffe0cf] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1 text-center sm:text-left">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                Relaterede behandlingsområder
              </div>
              <p className="text-sm text-[#593427] font-serif">
                Læs også om andre behandlinger ved smerter i bevægeapparatet
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Link
                to="/spaendinger"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Spændinger i nakke & skuldre →
              </Link>
              <Link
                to="/led-og-muskelsmerter"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Led & muskelsmerter →
              </Link>
              <Link
                to="/japansk-akupunktur"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Japansk akupunktur →
              </Link>
              <Link
                to="/oere-akupunktur"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Øre-akupunktur mod smerter →
              </Link>
              <Link
                to="/stress"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Stress & kropsro →
              </Link>
            </div>
          </div>

          {/* Hurtige genveje */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser & tilskud fra “danmark”
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumi Kita Thiele
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Samlet FAQ
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid i Risskov
            </Link>
          </div>

          {/* Contact and Booking Section */}
          <div className="pt-6">
            <div className="text-center max-w-2xl mx-auto mb-8 space-y-2">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Bestil tid til ryg- og lændeakupunktur
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f]">
                Klinikken ligger i rolige rammer på Kantorparken 11 i Risskov, få minutter fra Aarhus, med gratis parkering lige foran huset.
              </p>
            </div>
            <ContactForm />
          </div>

          {/* FAQ Section sidst på siden */}
          <div className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
            <FaqSection
              title="Ofte stillede spørgsmål om rygsmerter og iskias"
              subtitle="Få svar på hvordan nænsom akupunktur lindrer lændehold, dæmper iskiassmerter og afhjælper kroniske spændinger i ryggen hos Kita Akupunktur."
              faqs={[
                {
                  question: "Kan akupunktur hjælpe på både akutte lændehold og kroniske rygsmerter?",
                  answer:
                    "Ja, akupunktur er yderst effektivt til både pludseligt opståede hekseskud og længerevarende rygsmerter. Ved akutte lændehold virker behandlingen hurtigt krampestillende på de omkringliggende rygmuskler og dæmper den akutte inflammation, så du genvinder bevægeligheden. Ved kroniske rygsmerter arbejder vi dybere med at genskabe blodcirkulationen til stive led og afspænde nervesystemets vedvarende smertesignalering.",
                },
                {
                  question: "Hvordan kan akupunktur lindre iskiassmerter og udstråling i benet?",
                  answer:
                    "Iskiassmerter skyldes ofte, at den store iskiasnerve presses eller irriteres af dybe, spændte sædemuskler (f.eks. piriformis) eller låsninger i lændens facetled. Ved præcist at stimulere meridianpunkter i lænden, balden og langs benet afspændes det dybe muskelvæv. Dette fjerner trykket fra nerven, dæmper inflammationen og mindsker markant den jagende fornemmelse og snurren ned i benet.",
                },
                {
                  question: "Må jeg modtage akupunktur, hvis jeg har en diskusprolaps eller slidgigt i ryggen?",
                  answer: (
                    <>
                      Ja, akupunktur er en sikker og meget skånsom behandling ved både diskusprolaps og{" "}
                      <Link to="/led-og-muskelsmerter" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        slidgigt i rygsøjlen
                      </Link>
                      . Selvom akupunktur ikke genskaber slidt brusk, fjerner behandlingen de kraftige muskelspændinger, som kroppen automatisk danner som beskyttelse omkring det smertefulde led. Ved at reducere disse kramper og stimulere kroppens egne smertestillende stoffer oplever de fleste stor lindring.
                    </>
                  ),
                },
                {
                  question: "Hvor mange behandlinger skal jeg have, før min ryg får det bedre?",
                  answer: (
                    <>
                      Ved et akut lændehold eller hekseskud er 2-3 tætte behandlinger i løbet af en uge ofte nok til at bryde krampetilstanden og bringe kroppen i gang igen. Har du derimod døjet med rygsmerter gennem flere måneder eller år, anbefales typisk et sammenhængende forløb på 4-6 sessioner med ugentlige intervaller. Læs mere om tilskud fra Sygeforsikring “danmark” under{" "}
                      <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        priser og tilskud
                      </Link>
                      .
                    </>
                  ),
                },
                {
                  question: "Skal jeg ligge på maven under behandlingen, hvis min ryg er meget smerteplaget?",
                  answer: (
                    <>
                      Hvis du har stærke smerter og ikke kan ligge på maven, tilpasser jeg altid din lejring på briksen. Du kan ligge behageligt på siden med støttende puder mellem knæene eller sidde afslappet oprejst. I{" "}
                      <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        japansk akupunktur
                      </Link>{" "}
                      anvender vi desuden effektive distale punkter på underarme, ben og ører, så vi kan lindre rygsmerterne effektivt, uden at du behøver at ligge i en ubehagelig stilling.
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>
    </>
  );
}
